import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import initBootstrapComponents from '../../utils/bootstrap';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import axios from "axios";
import { useBasket } from '../../context/BasketContext';
import { Riple } from "react-loading-indicators";
import Modal from 'react-bootstrap/Modal';
import { API_BASE_URL } from '../../constants/api';

// Import SVG icons
import transmissionIcon from '../../assets/img/icons/vehicle-transmission.svg';
import fuelTypeIcon from '../../assets/img/icons/vehicle-fuel-type.svg';
import bodyTypeIcon from '../../assets/img/icons/vehicle-body-type.svg';
import engineSizeIcon from '../../assets/img/icons/vehicle-engine-size.svg';
import doorIcon from '../../assets/img/icons/vehicle-door.svg';
import seatIcon from '../../assets/img/icons/vehicle-seat.svg';

const EventVehicleAvailability = () => {

    const { vehicleId, eventId } = useParams();
    const { addToBasket, basket } = useBasket();
    const navigate = useNavigate();
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [vehicle, setVehicle] = useState(null);
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [eventDetails, setEventDetails] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [bookingErrorMessage, setBookingErrorMessage] = useState("");

    useEffect(() => {
        initBootstrapComponents();
        fetchEventDetails();
    }, []);

    const fetchEventDetails = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/events/${eventId}/details?key=7678356c642c91a255471d162205e0f3`);
            const event = response.data.data;
            setEventDetails(event);

            // Build available dates from the event's date range
            const dates = generateDateRange(new Date(event.start_at), new Date(event.end_at))
                .filter(date => {
                    const today = new Date();
                    today.setHours(0,0,0,0);
                    return date >= today; // Still filter out past dates, but we'll handle future dates in UI
                });
            dates.sort((a, b) => a - b);
            setAvailableDates(dates);

            // Select today if it's within range, otherwise the first available date
            if (dates.length > 0) {
                const todayDate = dates.find(date => isToday(date));
                setSelectedDate(todayDate || dates[0]);
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.error('Error fetching event details:', error);
            setLoading(false);
        }
    }

    useEffect(() => {
        if (selectedDate) {
            setSelectedSlot(null);
            fetchVehicleAndSlots(selectedDate);
        }
    }, [selectedDate]);

    const generateDateRange = (startDate, endDate) => {
        const dates = [];
        const currentDate = new Date(startDate);
        currentDate.setHours(0,0,0,0);
        const end = new Date(endDate);
        end.setHours(0,0,0,0);

        while (currentDate <= end) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates;
    };

    const formatDateParam = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const isToday = (date) => {
        const today = new Date();
        return date.getFullYear() === today.getFullYear() &&
               date.getMonth() === today.getMonth() &&
               date.getDate() === today.getDate();
    };

    const isFutureDate = (date) => {
        const today = new Date();
        today.setHours(0,0,0,0);
        const checkDate = new Date(date);
        checkDate.setHours(0,0,0,0);
        return checkDate > today;
    };

    const isSlotInPast = (slotStartAt) => {
        const now = new Date();
        const slotTime = new Date(slotStartAt);
        const tenMinutesAgo = new Date(now.getTime() - 10 * 60 * 1000);
        return slotTime <= tenMinutesAgo;
    };

    const isTomorrowSlot = (slot) => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0,0,0,0);
        
        const slotDate = new Date(slot.start_at);
        slotDate.setHours(0,0,0,0);
        
        return slotDate.getTime() === tomorrow.getTime();
    };

    const isSameDay = (date1, date2) => {
        return date1.getFullYear() === date2.getFullYear() &&
               date1.getMonth() === date2.getMonth() &&
               date1.getDate() === date2.getDate();
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const fetchVehicleAndSlots = async (date) => {
        setSlotsLoading(true);
        try {
            const dateParam = formatDateParam(date);
            const response = await axios.get(`${API_BASE_URL}/api/events/${eventId}/slot-bookings?key=7678356c642c91a255471d162205e0f3&format=vehicles&start_at=${dateParam}&end_at=${dateParam}`);

            // The API doesn't return a bookings count on each slot, so summarise
            // the bookings across every vehicle by event_slot_id to get the total
            // number of bookings for each slot.
            const bookingsCountBySlot = {};
            Object.values(response.data.data).forEach(vehicleData => {
                Object.values(vehicleData.bookings || {}).forEach(booking => {
                    const slotId = booking.event_slot_id;
                    bookingsCountBySlot[slotId] = (bookingsCountBySlot[slotId] || 0) + 1;
                });
            });

            let foundVehicle = false;
            Object.values(response.data.data).forEach(vehicleData => {

                if(vehicleData.vehicle.id == vehicleId) {
                    foundVehicle = true;

                    // Keep the vehicle detail card populated
                    setVehicle(vehicleData.vehicle);

                    const slotsArray = [];
                    Object.values(vehicleData.slots).forEach(slotData => {
                        slotsArray.push({
                            ...slotData,
                            bookings_count: bookingsCountBySlot[slotData.id] || 0
                        });
                    });
                    slotsArray.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

                    setSlots(slotsArray);
                }
            });

            // If the vehicle isn't present for this day, clear its slots but keep the card
            if (!foundVehicle) {
                setSlots([]);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setSlotsLoading(false);
            setLoading(false);
        }
    };

    const formatTimeSlot = (dateTimeString) => {
        return new Date(dateTimeString).toLocaleTimeString('en-GB', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).toLowerCase();
    };

    const handleSlotSelect = (slot) => {
        setSelectedSlot(slot);
    };

    const handleBookTestDrive = async () => {
        if (!selectedSlot) return;
        
        // Check if the slot already exists in the basket
        const existingSlots = basket?.slots || [];
        const slotAlreadyExists = existingSlots.some(existingSlot => 
            existingSlot.id === selectedSlot.id
        );
        
        if (slotAlreadyExists) {
            setBookingErrorMessage('This time slot is already reserved for you');
            return;
        }
        
        try {
            // First lock the slot
            const bookingId = await lockSlots(selectedSlot);

            if (!bookingId) return;

            // Then add to basket
            const newSlot = {
                id: selectedSlot.id,
                booking_id: bookingId,
                vehicle: vehicle,
                start_at: selectedSlot.start_at,
                date: new Date(selectedSlot.start_at)
            };
            
            // Add new slot to existing slots
            addToBasket({
                slots: [...existingSlots, newSlot],
                event_id: eventId,
                event_name: eventDetails.name,
            });

            // Show the modal for navigation choice
            setShowModal(true);
        } catch (error) {
            console.error('Error in booking process:', error);
            // You might want to show an error message to the user here
        }
    };

    const handleConfirmBooking = () => {
        setShowModal(false);
        navigate('/basket-confirmation');
    };

    const handleKeepBrowsing = () => {
        setShowModal(false);
        navigate(`/event/${eventId}/vehicle-list`);
    };

    const lockSlots = async (slot) => {
        setLoading(true);
        setBookingErrorMessage("");
        try {
            const formDataObj = new FormData();
            formDataObj.append('slots[0][slot]', parseInt(slot.id));
            formDataObj.append('slots[0][vehicle]', parseInt(vehicle.id));
            const ambassadorId = localStorage.getItem('ambassador_id');
            formDataObj.append('ambassador_id', ambassadorId);
            formDataObj.append('key', '7678356c642c91a255471d162205e0f3');
            const token = localStorage.getItem('ambassador_token');
            const resp = await axios.post(`${API_BASE_URL}/api/events/bookings/book`, formDataObj, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'multipart/form-data'
                },
                withCredentials: true
            });


            const errorMessage = resp.data.data.errmessage;
            if (errorMessage) {
                setBookingErrorMessage(errorMessage);
                return null;
            } else {
                // Extract booking IDs from the response
                const bookingIds = resp.data.data.bookings.map(booking => booking.id);
                // const existingBookingIds = JSON.parse(localStorage.getItem('bookingIds') || '[]');
                // const updatedBookingIds = [
                //     ...existingBookingIds,
                //     ...bookingIds.map(bookingId => ({
                //         bookingId: bookingId,
                //         slotId: parseInt(slot.id)
                //     }))
                // ];
                // localStorage.setItem('bookingIds', JSON.stringify(updatedBookingIds));
                // If the API returns a message, clear any previous error
                setBookingErrorMessage("");

                return bookingIds[0];
            }
            


        } catch (error) {
            console.error('Error locking slots:', error);
            // If the error response contains a message, display it
            const apiMessage = error.response?.data?.data?.message;
            if (apiMessage) {
                setBookingErrorMessage(apiMessage);
            } else {
                setBookingErrorMessage("An error occurred while booking the slot.");
            }
            throw error;
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex flex-column justify-content-between min-vh-100">
                <Header />
                <div className="d-flex justify-content-center align-items-center flex-grow-1">
                    <Riple color="#0052BE" size="medium" />
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column justify-content-between min-vh-100" id="body-container">
            <Header />
            
            <main>
                <section className="bg-motability-mid-black-5 py-32 pt-lg-96 pb-lg-87">
                    <div className="container">
                        <div className="d-flex flex-column align-items-start mb-4">
                            <Link to={`/event/${eventId}/vehicle-list`} className="d-inline-block fw-medium text-decoration-none">
                                ‹ <span className="d-inline-block text-decoration-underline">Back to vehicle selection</span>
                            </Link>
                        </div>

                        <h1 className="text-primary mb-4 mb-lg-5">Vehicle details and booking</h1>

                        <div className="row row-gap-4 row-gap-lg-0 gx-xxl-5">
                            {/* Vehicle Details Card */}
                            <div className="col-lg-6">
                                <div className="bg-white rounded-4 p-4 p-lg-32">
                                    <h2 className="text-primary h3 mb-3">{vehicle?.manufacturer?.name} {vehicle?.model}</h2>
                                    
                                    <div className="mb-4">
                                        <img 
                                            src={vehicle?.images_array[0]}
                                            alt={vehicle?.name}
                                            className="img-fluid rounded-3 w-100"
                                        />
                                    </div>

                                    <p className="mb-3">{vehicle?.is_adapted ? 'Adapted Vehicle' : 'Unadapted Vehicle'}</p>

                                    <h3 className="text-primary h4 mb-3">Features</h3>
                                    <p className="mb-4">{vehicle?.description}</p>

                                    <div className="row g-4">
                                        <div className="col-6 col-md-4">
                                            <div className="d-flex flex-column align-items-center text-center">
                                                <div className="bg-motability-light-blue rounded-circle p-3 mb-2">
                                                    <img src={transmissionIcon} alt="Transmission" className="text-primary" />
                                                </div>
                                                <p className="fw-semibold mb-1">Transmission</p>
                                                <p className="mb-0">{vehicle?.transmission?.name}</p>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <div className="d-flex flex-column align-items-center text-center">
                                                <div className="bg-motability-light-blue rounded-circle p-3 mb-2">
                                                    <img src={fuelTypeIcon} alt="Fuel type" className="text-primary" />
                                                </div>
                                                <p className="fw-semibold mb-1">Fuel type</p>
                                                <p className="mb-0">{vehicle?.fuel_type?.name}</p>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <div className="d-flex flex-column align-items-center text-center">
                                                <div className="bg-motability-light-blue rounded-circle p-3 mb-2">
                                                    <img src={bodyTypeIcon} alt="Body type" className="text-primary" />
                                                </div>
                                                <p className="fw-semibold mb-1">Body type</p>
                                                <p className="mb-0">{vehicle?.body_type?.name}</p>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <div className="d-flex flex-column align-items-center text-center">
                                                <div className="bg-motability-light-blue rounded-circle p-3 mb-2">
                                                    <img src={engineSizeIcon} alt="Engine size" className="text-primary" />
                                                </div>
                                                <p className="fw-semibold mb-1">Engine size</p>
                                                <p className="mb-0">{vehicle?.engine_size}</p>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <div className="d-flex flex-column align-items-center text-center">
                                                <div className="bg-motability-light-blue rounded-circle p-3 mb-2">
                                                    <img src={doorIcon} alt="Number of doors" className="text-primary" />
                                                </div>
                                                <p className="fw-semibold mb-1">Number of doors</p>
                                                <p className="mb-0">{vehicle?.doors?.name}</p>
                                            </div>
                                        </div>
                                        <div className="col-6 col-md-4">
                                            <div className="d-flex flex-column align-items-center text-center">
                                                <div className="bg-motability-light-blue rounded-circle p-3 mb-2">
                                                    <img src={seatIcon} alt="Number of seats" className="text-primary" />
                                                </div>
                                                <p className="fw-semibold mb-1">Number of seats</p>
                                                <p className="mb-0">{vehicle?.seats?.name}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Booking Card */}
                            <div className="col-lg-6">
                                <div className="bg-white rounded-4 p-4 p-lg-32">
                                    <h2 className="text-primary h3 mb-4">
                                        Book a test drive with vehicle number {vehicle?.number}
                                    </h2>

                                    {/* Date Selection */}
                                    <div className="mb-4">
                                        <p className="text-primary fw-semibold mb-3">Choose a date</p>
                                        <div className="d-flex flex-wrap gap-2">
                                            {availableDates.map((date) => {
                                                const isDateDisabled = isFutureDate(date);
                                                return (
                                                    <button
                                                        key={date.toISOString()}
                                                        type="button"
                                                        className={`btn ${
                                                            selectedDate && isSameDay(selectedDate, date)
                                                            ? 'btn-primary text-white'
                                                            : isDateDisabled 
                                                            ? 'bg-light text-muted'
                                                            : 'bg-motability-light-orange'
                                                        } rounded-4 px-4 py-2`}
                                                        onClick={() => !isDateDisabled && setSelectedDate(date)}
                                                        disabled={isDateDisabled}
                                                        style={isDateDisabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                                                    >
                                                        {formatDate(date)}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Time Selection */}
                                    <div className="mb-4">
                                        <p className="text-primary fw-semibold mb-3">Choose a time</p>
                                        {slotsLoading ? (
                                            <div className="d-flex justify-content-center py-3">
                                                <Riple color="#0052BE" size="small" />
                                            </div>
                                        ) : (
                                        <div className="d-flex flex-wrap gap-2">
                                            {slots.map((slot) => {
                                                const slotLimit = slot.limit;
                                                const isSlotDisabled = isFutureDate(slot.start_at) || isSlotInPast(slot.start_at) || slot.is_booked_for_vehicle || (slotLimit != null && slot.bookings_count >= slotLimit);
                                                return (
                                                    <button
                                                        key={slot.id}
                                                        type="button"
                                                        className={`btn ${
                                                            selectedSlot?.id === slot.id 
                                                            ? 'btn-primary text-white' 
                                                            : isSlotDisabled
                                                            ? 'bg-light text-muted'
                                                            : 'bg-motability-light-orange'
                                                        } rounded-pill px-4`}
                                                        onClick={() => !isSlotDisabled && handleSlotSelect(slot)}
                                                        disabled={isSlotDisabled}
                                                        style={isSlotDisabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                                                    >
                                                        {formatTimeSlot(slot.start_at)}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        )}
                                    </div>

                                    {bookingErrorMessage && (
                                        <div className="alert alert-danger my-3" role="alert">
                                            {bookingErrorMessage}
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        className="btn btn-primary px-4"
                                        onClick={handleBookTestDrive}
                                        disabled={!selectedSlot}
                                    >
                                        Add to basket
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                </section>
            </main>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
                aria-labelledby="add-slot-to-basket-modal"
            >
                <Modal.Header className="pt-64 pb-5 border-0 justify-content-center">
                    <h4 className="text-primary text-center mb-0" id="add-slot-to-basket-modal">
                        Add slot to Basket
                    </h4>
                </Modal.Header>

                <Modal.Footer className="gap-1 gap-lg-39 px-3 pt-0 pb-64 justify-content-center border-0">
                    <button 
                        type="button" 
                        className="btn btn-primary m-0 px-lg-29"
                        onClick={handleConfirmBooking}
                    >
                        Go to Checkout
                    </button>

                    <button 
                        type="button" 
                        className="btn btn-outline-primary fw-normal m-0 px-lg-29"
                        onClick={handleKeepBrowsing}
                    >
                        Continue booking
                    </button>
                </Modal.Footer>
            </Modal>

            <Footer />
        </div>
    );
};

export default EventVehicleAvailability;
